<template>
  <div v-if="showStatesDialog" class="modal-overlay">
    <div class="modal-dialog">
      <div class="modal-header">
        <h3>{{ dialogTitle }}</h3>
        <button @click="closeDialog"><i class="fas fa-close"></i></button>
      </div>

      <!-- Lease states table -->
      <div class="lease-states-table">
        <div class="table-header">
          <div class="table-column narrow-column">ShortCode</div>
          <div class="table-column wide-column">Description</div>
          <div class="table-column">Modified By</div>
          <div class="table-column">On</div>
          <div class="table-column narrow-column">Escalate To</div>
          <div class="table-column">Escalate On</div>
          <div class="table-column narrow-column">Actions</div>
        </div>
        <div class="table-body">
          <div class="no-states" v-if="currentStates.length === 0">
            There are no existing states for this
            {{ isPropertyState ? 'property' : 'lease/tenant' }}. Please add one
            below.
          </div>
          <div v-else>
            <div
              v-for="state in currentStates"
              :key="state.id"
              class="table-row"
            >
              <div class="table-column narrow-column">
                {{ state.shortCode }}
              </div>
              <div class="table-column wide-column">
                {{ state.Description }}
              </div>
              <!-- Increased width for Description -->
              <div class="table-column">{{ state.ModifiedBy }}</div>
              <div class="table-column">
                {{ formatDateTime(state.ModifiedOn) }}
              </div>
              <div class="table-column narrow-column">
                {{ state.EscalatesTo }}
              </div>
              <div class="table-column">
                {{ state.EscalatesOn ? formatDate(state.EscalatesOn) : '' }}
              </div>
              <!-- Conditional date check -->
              <div class="table-column narrow-column">
                <button @click="deleteState(state.id)">Delete</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="choose-from">Choose from</div>
      <!-- State type buttons -->
      <div class="state-type-buttons">
        <button
          v-for="stateType in sortedAvailableStateTypes"
          :key="stateType.id"
          @click="addState(stateType.id)"
          :style="{
            color: stateType.foreground || '#333',
            backgroundColor: stateType.background || '#ededed',
          }"
        >
          <span
            style="
              font-family: 'consolas';
              font-weight: bold;
              margin-right: 6px;
            "
            >{{ stateType.shortCode }}</span
          >
          <span style="font-size: 0.9em">{{ stateType.Description }}</span>
        </button>
      </div>

      <!-- Cancel and Save buttons -->
      <div class="modal-footer">
        <button @click="closeDialog">Cancel</button>
      </div>
    </div>
  </div>
</template>
<script>
import axios from '@/axiosConfig'; // Adjust based on your axios configuration

export default {
  props: {
    leaseId: {
      type: [Number, null],
      required: false,
    },
    propertyId: {
      type: [Number, null],
      required: false,
    },
    showStatesDialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      leaseStates: [], // To hold the list of lease states
      propertyStates: [], // To hold the list of property states
      availableStateTypes: [], // To hold the available state types
    };
  },
  computed: {
    isPropertyState() {
      return this.propertyId && !this.leaseId;
    },
    currentStates() {
      return this.isPropertyState ? this.propertyStates : this.leaseStates;
    },
    dialogTitle() {
      return this.isPropertyState
        ? 'View or Modify Property State'
        : 'View or Modify Lease State';
    },
    entityId() {
      return this.isPropertyState ? this.propertyId : this.leaseId;
    },
    sortedAvailableStateTypes() {
      return [...this.availableStateTypes].sort((a, b) => {
        const bgA = a.background || '#ededed';
        const bgB = b.background || '#ededed';
        return bgA.localeCompare(bgB);
      });
    },
  },
  methods: {
    formatDateTime(dateString) {
      if (!dateString) return ''; // Return an empty string if the date is invalid

      const date = new Date(dateString);

      // Format the date as "MM/DD/YY hh:mmAM/PM"
      return date.toLocaleString('en-US', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    },

    fetchStates() {
      if (this.isPropertyState) {
        axios
          .get(`/api/property/${this.propertyId}/states`)
          .then((response) => {
            this.propertyStates = response.data;
          })
          .catch((error) => {
            console.error('Error fetching property states:', error);
          });
      } else {
        axios
          .get(`/api/lease/${this.leaseId}/states`)
          .then((response) => {
            this.leaseStates = response.data;
          })
          .catch((error) => {
            console.error('Error fetching lease states:', error);
          });
      }
    },

    fetchLeaseStates() {
      // Keep this method for backward compatibility
      this.fetchStates();
    },

    fetchAvailableStateTypes() {
      // Determine useFor parameter based on dialog type
      // useFor: 1 = lease, 2 = property
      const useFor = this.isPropertyState ? 2 : 1;

      axios
        .get(`/api/lease-state-types?useFor=${useFor}`)
        .then((response) => {
          this.availableStateTypes = response.data;
        })
        .catch((error) => {
          console.error('Error fetching available state types:', error);
        });
    },

    addState(stateId) {
      const endpoint = this.isPropertyState
        ? '/api/property-state/add'
        : '/api/lease-state/add';
      const payload = this.isPropertyState
        ? { propertyId: this.propertyId, stateId }
        : { leaseId: this.leaseId, stateId };

      axios
        .post(endpoint, payload)
        .then(() => {
          this.fetchStates(); // Refresh the table
        })
        .catch((error) => {
          console.error('Error adding state:', error);
        });
    },

    deleteState(stateId) {
      const endpoint = this.isPropertyState
        ? `/api/property-state/${stateId}`
        : `/api/lease-state/${stateId}`;

      axios
        .delete(endpoint)
        .then(() => {
          this.fetchStates(); // Refresh the table
        })
        .catch((error) => {
          console.error('Error deleting state:', error);
        });
    },

    closeDialog() {
      this.$emit('update:showStatesDialog', false);
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
  },
  watch: {
    showStatesDialog(newVal) {
      if (newVal) {
        this.fetchStates();
        this.fetchAvailableStateTypes();
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  align-items: center;
  z-index: 10050; /* Above unpaid-balances and similar popups */
}

.modal-dialog {
  background-color: white;
  position: absolute;
  top: 100px;
  left: 100px;
  padding: 20px;
  width: 900px;
  border-radius: 5px;
  min-height: 370px;
  z-index: 10051;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #333;
  color: #eee;
  margin: -20px;
  padding: 12px;
  height: 35px;
  margin-bottom: 4px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  margin-bottom: 16px;
}

.lease-states-table {
  max-height: 200px;
  overflow-y: scroll;
  margin-bottom: 20px;
  font-size: 0.8em;
}

.table-header,
.table-row {
  display: flex;
}

.table-column {
  width: 100px;
  padding: 5px;
}

.state-type-buttons button {
  margin-right: 10px;
  margin-top: 10px;
  padding: 6px;
  border: 1pt solid #aaa;
  border-radius: 5px;
  background: #a080a8;
  color: white;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.table-column {
  flex: 2; /* Or any appropriate width that works for your layout */
}

.narrow-column {
  flex: 1;
}

.wide-column {
  flex: 4; /* This will make the description column twice as wide */
}

.table-header .table-column {
  border: 1pt solid #eee;
  background: #ddd;
}

.table-body .table-column {
  border: 0.5pt solid #eee;
  border-collapse: collapse;
}

.no-states {
  margin-top: 15px;
}
.choose-from {
  margin-top: 10px;
  margin-bottom: 10px;
}

.modal-footer button {
  position: absolute;
  bottom: 11px;
  right: 32px;
}
</style>
